<template>
	<section
		id="hero-banner"
		class="bg-[url('/banner-home.jpg')] h-screen flex flex-col justify-center items-center gap-3 md:gap-5 bg-cover bg-bottom mb-16 md:mb-32 relative"
	>
		<div class="absolute inset-0 bg-black bg-opacity-40"></div>
		<Container>
			<h1
				class="text-white heading-1 text-center relative max-w-xl mx-auto z-10 !leading-tight mb-5"
			>
				Rafting & ATV All-In-One Ubud Adventures
			</h1>
			<p
				class="text-white text-xl text-center leading-relaxed mx-auto max-w-3xl px-4 relative z-10"
			>
				Enjoy safe and thrilling rafting or kayaking, family-friendly ATV rides,
				a laid-back VW Safari tour, and easy cycling routes through scenic
				countryside.
			</p>
		</Container>
	</section>

	<!-- <VoucherSection :coupons="coupons" /> -->

	<Container>
		<section id="activities" class="mb-16 md:mb-20">
			<div class="mb-3 md:mb-10">
				<!-- Header with navigation -->
				<div class="flex items-center justify-between">
					<div class="flex items-center justify-center gap-3">
						<h2 class="md:text-left text-center heading-2">
							Top Trending Activity in Bali
						</h2>
					</div>

					<!-- Navigation arrows -->
					<div class="gap-10 hidden md:flex">
						<button
							@click="navigateLeft"
							:disabled="!canNavigateLeft || showAllActivities"
							class="w-9 h-9 border-2 border-black rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M15 19l-7-7 7-7"
								></path>
							</svg>
						</button>
						<button
							@click="navigateRight"
							:disabled="!canNavigateRight || showAllActivities"
							class="w-9 h-9 border-2 border-black rounded-full flex items-center justify-center transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
						>
							<svg
								class="w-5 h-5"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									stroke-linecap="round"
									stroke-linejoin="round"
									stroke-width="2"
									d="M9 5l7 7-7 7"
								></path>
							</svg>
						</button>
					</div>
				</div>
			</div>

			<div
				class="grid grid-cols-1 md:grid-cols-3 gap-6 transition-all duration-300 ease-in-out"
			>
				<ActivityCard
					v-for="productCategory in displayProducts"
					:key="productCategory.id"
					:title="productCategory.name"
					:price="formatCurrency(Number(productCategory.products?.[0].price))"
					:description="productCategory?.short_description"
					:features="
						[
							productCategory.products?.[0].activity_hours,
							productCategory.products?.[0].booking_method,
							productCategory.products?.[0].location,
						].filter(Boolean)
					"
					:image="productCategory.images?.[0]?.image"
					:badge-text="
						productCategory.id === firstProductCategoryId ? 'Most popular' : ''
					"
					:slug="productCategory.slug"
				/>
			</div>

			<div class="flex items-center justify-center mt-10">
				<button
					@click="toggleShowAllActivities"
					class="border border-black body-1 w-full md:w-fit text-center px-0 md:px-36 py-5 md:py-2 rounded-md hover:bg-black hover:text-white text-black transition-colors"
				>
					{{ showAllActivities ? "Show Less" : "See All Activities" }}
				</button>
			</div>
		</section>

		<section id="e-bikes" class="mb-8 md:mb-24">
			<p class="heading-2 mb-8">E-Bike Trekking Bali</p>
			<div class="grid grid-cols-1 md:grid-cols-2 gap-4">
				<div
					class="bg-[url('/e-bikes.jpeg')] bg-cover bg-center rounded-md h-[200px] md:h-full"
				></div>
				<div class="space-y-4">
					<EBikeCard
						v-for="productCategory in bikesCategories"
						:key="productCategory.id"
						:title="productCategory.name"
						:description="productCategory?.short_description"
						:price="formatCurrency(Number(productCategory.products?.[0].price))"
						:image="productCategory.images?.[0]?.image"
						:href="`/activities/${productCategory.slug}`"
					/>
				</div>
			</div>
		</section>
	</Container>

	<section
		id="rafting"
		class="relative overflow-hidden text-white mb-24 py-48 hidden md:block"
	>
		<img
			src="/kayaking-banner.png"
			alt="Kayaking banner"
			class="absolute inset-0 w-full h-full object-cover object-center z-0"
		/>

		<Container class="relative z-10">
			<div class="flex flex-col w-1/2 justify-end mt-36">
				<h3 class="heading-1 mb-3 leading-snug">
					Kayaking in Bali <br />
					Safe, Scenic, and Fun
				</h3>
				<p class="sub-heading-1 mb-14 leading-normal">
					Paddle through calm river views with expert guides and full safety
					gear. <br />
					Great for beginners, couples, and families—easy booking, unforgettable
					moments.
				</p>
				<NuxtLink to="/kayaking">
					<button
						class="bg-white hover:bg-[#EFAD1E] text-black px-4 py-2 rounded-md flex items-center w-fit body-1"
					>
						Learn More
					</button>
				</NuxtLink>
			</div>
		</Container>
	</section>

	<section id="more-than" class="md:mb-32 mt-16 md:mt-0 mb-16">
		<Container>
			<div
				class="flex flex-col justify-center items-center gap-2 md:gap-3 mb-24"
			>
				<h3 class="heading-2 font-medium max-w-2xl mx-auto">
					Relax and Recharge
				</h3>
				<p class="text-center sub-heading-1 md:block hidden">
					Let our warm hospitality, flavorful dishes, and refreshing drinks
					welcome <br />
					you into our restaurant with a view of Bali’s lush rice paddies.
				</p>
				<p class="text-center body-1 md:hidden">
					Let our warm hospitality, flavorful dishes, and refreshing drinks
					welcome you into our restaurant with a view of Bali’s lush rice
					paddies.
				</p>
			</div>
		</Container>

		<div class="md:grid grid-cols-6 w-full gap-4 h-[450px] hidden">
			<HoverGridItem
				v-for="index in 5"
				:key="index"
				:index="index - 1"
				:is-expanded="
					hoveredIndex === index - 1 || (hoveredIndex === null && index === 3)
				"
				@mouseenter="hoveredIndex = $event"
				@mouseleave="hoveredIndex = null"
			/>
		</div>

		<div class="grid grid-cols-4 w-full gap-2 h-[450px] md:hidden">
			<HoverGridItem
				v-for="index in 3"
				:key="index"
				:index="index - 1"
				:is-expanded="
					hoveredIndex === index - 1 || (hoveredIndex === null && index === 2)
				"
				@mouseenter="hoveredIndex = $event"
				@mouseleave="hoveredIndex = null"
			/>
		</div>
	</section>

	<section
		id="video"
		class="bg-[url('/video-banner.jpg')] bg-cover bg-center text-white md:mb-32 mb-16"
	>
		<div
			class="mx-auto flex flex-col py-20 md:py-32 max-w-sm md:max-w-3xl md:px-0 px-6"
		>
			<p class="sub-heading-1 mb-1 md:mb-3">Ready?</p>
			<p class="heading-2 mb-4 md:mb-10">See It In Action!</p>
			<p class="body-2 !leading-loose mb-12 md:mb-5">
				Paddle through 12 km of rushing Ayung River with rafting or kayaking,
				ride ATVs across muddy trails, tunnels, and rice fields, or cycling and
				trekking into Bali’s breathtaking nature and culture.
			</p>
			<div
				class="flex items-center gap-3 cursor-pointer"
				@click="openYouTubeVideo"
			>
				<Icon name="gridicons:play" class="text-2xl" />
				<p class="body-1">Tap to watch</p>
			</div>
		</div>
	</section>

	<section id="what-makes-us" class="md:mb-32 mb-16">
		<Container>
			<h2 class="heading-2 mb-3 md:mb-10 text-center md:text-left">
				What Makes Us Different?
			</h2>

			<!-- Desktop Grid Layout -->
			<div
				class="hidden md:grid grid-cols-6 gap-4 h-[450px] transition-all duration-500 ease-out"
			>
				<GridItem
					v-for="(item, index) in 4"
					:key="index"
					:index="index"
					:is-expanded="
						hoveredIndex === index || (hoveredIndex === null && index === 0)
					"
					@mouseenter="hoveredIndex = $event"
					@mouseleave="hoveredIndex = null"
				/>
			</div>
		</Container>

		<!-- Mobile/Tablet Horizontal Scrollable Layout -->
		<div class="md:hidden">
			<div class="flex gap-2 overflow-x-auto pb-4 scrollbar-hide px-6">
				<MobileGridItem
					v-for="(item, index) in 4"
					:key="index"
					:index="index"
					:is-expanded="selectedIndex === index"
					@click="selectedIndex = selectedIndex === index ? null : index"
				/>
			</div>
		</div>
	</section>

	<FAQ />

	<section class="bg-[#F8F8F8] mb-20 py-10 md:py-28 hidden md:block">
		<Container>
			<div class="text-center mb-16">
				<h2 class="heading-2 text-black mb-2 max-w-2xl mx-auto">
					What They Say About Us
				</h2>
				<p class="text-gray-700 sub-heading-1 px-6 md:px-0">
					Trusted by travelers who value authenticity, safety, and quality from
					start to finish.
				</p>
			</div>

			<div class="relative">
				<!-- Navigation Arrows -->
				<button
					class="absolute left-0 top-1/2 transform -translate-y-1/2 w-9 h-9 border-2 border-gray-400 rounded-full flex items-center justify-center text-gray-400 backdrop-blur-sm z-10"
				>
					<svg
						class="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M15 19l-7-7 7-7"
						></path>
					</svg>
				</button>

				<button
					class="absolute right-0 top-1/2 transform -translate-y-1/2 w-9 h-9 border-2 border-gray-400 rounded-full flex items-center justify-center text-gray-400 backdrop-blur-sm z-10"
				>
					<svg
						class="w-5 h-5"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
					>
						<path
							stroke-linecap="round"
							stroke-linejoin="round"
							stroke-width="2"
							d="M9 5l7 7-7 7"
						></path>
					</svg>
				</button>

				<!-- Testimonials Grid -->
				<div class="grid grid-cols-1 md:grid-cols-3 gap-4 px-16">
					<TestimonialCard
						v-for="(testimonial, index) in testimonials.slice(0, 3)"
						:key="index"
						:testimonial="testimonial"
						:is-mobile="false"
					/>
				</div>
			</div>
		</Container>
	</section>

	<!-- Mobile Testimonials Section -->
	<section class="md:hidden bg-[#F8F8F8] py-16 mb-16">
		<div class="text-center mb-8">
			<h2 class="heading-2 text-black mb-2">What They Say About Us</h2>
			<p class="text-gray-700 body-1 px-6">
				Trusted by travelers who value authenticity, safety, and quality from
				start to finish.
			</p>
		</div>

		<!-- Mobile Scrollable Testimonials -->
		<div class="overflow-x-auto scrollbar-hide">
			<div class="flex gap-4 w-max px-6 pb-4">
				<TestimonialCard
					v-for="(testimonial, index) in testimonials"
					:key="index"
					:testimonial="testimonial"
					:is-mobile="true"
				/>
			</div>
		</div>
	</section>

	<section id="more-than" class="md:mb-32 mb-24">
		<Container>
			<div
				class="flex flex-col justify-center items-center gap-2 md:gap-4 mb-5 md:mb-14"
			>
				<h3 class="heading-2">Explore More of Bali</h3>
				<p class="text-center sub-heading-1 md:block hidden max-w-2xl mx-auto">
					Wander the other sides of Bali through lush forests, sacred temples,
					and quite shores.
				</p>
				<p class="text-center md:hidden leading-relaxed body-1">
					Wander the other sides of Bali through lush forests, sacred temples,
					and quite shores.
				</p>
			</div>

			<div class="grid grid-cols-1 md:grid-cols-3 gap-8">
				<CardBlog
					v-for="(item, index) in homeArticles"
					:key="`article-${index}-${item.id}`"
					:articles="item"
				/>
			</div>
		</Container>
	</section>

	<section id="experience" class="bg-black md:mb-32 mb-16">
		<Container class="md:py-28 py-16">
			<div class="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 items-start">
				<!-- Left Column - Heading and Button -->
				<div class="flex flex-col justify-between h-full">
					<h2
						class="heading-2 !font-normal text-white !leading-relaxed hidden md:block"
					>
						Surya Bintang Adventures Experience As <br />
						Best Ubud Rafting In Bali And ATV With <br />
						Safety And Comfort
					</h2>
					<h2 class="sub-heading-1 text-white !leading-relaxed md:hidden mb-8">
						Surya Bintang Adventures Experience As Best Ubud Rafting In Bali And
						ATV With Safety And Comfort
					</h2>
					<button
						class="px-7 py-5 md:py-2 font-medium rounded-lg body-1 bg-[#EFAD1E] text-black w-fit"
					>
						See More
					</button>
				</div>

				<!-- Right Column - Descriptive Text -->
				<div class="space-y-6 text-white body-2 !leading-loose">
					<p>
						As a trusted adventure provider, Surya Bintang Adventures offers
						exciting activities designed for your safety and satisfaction. We
						have run thousands of people for activities like rafting, kayaking,
						ATV riding, cycling, and VW Safari. Every experience comes with full
						insurance, safety gear, expert guides, and a certified river team—so
						even families with young kids can join in without a worry.
					</p>

					<p>
						Our facilities include secure lockers, soft towels, hairdryers, and
						showers with adjustable hot or cold water to suit your needs. We
						have a souvenir stand that sells t-shirts, shorts, and flip-flops as
						souvenirs or for changing your outfit. Beyond that, we have a
						welcoming restaurant, where you can enjoy the delicious food with
						cool breezes and stunning rice field views. It's the perfect spot to
						relax, recharge, and soak in Bali's natural charm.
					</p>

					<p>
						At Surya Bintang Adventures, safety and comfort aren't just
						promises—It's our priorities, so you can fully enjoy every moment of
						your Bali experience.
					</p>
				</div>
			</div>
		</Container>
	</section>

	<section id="commitment" class="md:mb-32 mb-16">
		<Container>
			<p
				class="text-center heading-2 !font-normal px-0 md:px-10 leading-relaxed"
			>
				With Our Commitment To Excellence, Quality Is Our Promise, Safety Is Our
				Priority, And Fun Is Our Guarantee. Your Best Vacation Moments Start
				Here.
			</p>
		</Container>
	</section>
</template>

<script setup lang="ts">
import ActivityCard from "@/components/ActivityCard.vue";
import GridItem from "@/components/GridItem.vue";
import MobileGridItem from "@/components/MobileGridItem.vue";
import TestimonialCard from "@/components/TestimonialCard.vue";
import HoverGridItem from "@/components/HoverGridItem.vue";

const { formatCurrency } = useMoneyFormat();
const { href } = useCanonical();
const config = useRuntimeConfig();
const { t: $t } = useI18n();

const siteOrigin = computed(() =>
	String(
		config.public.BASE_URL ?? "https://suryabintangadventures.com",
	).replace(/\/$/, ""),
);

const structuredDataHome = computed(() => {
	const siteRoot = `${siteOrigin.value}/`;
	return {
		"@context": "https://schema.org",
		"@graph": [
			{
				"@type": "WebSite",
				"@id": `${siteRoot}#website`,
				url: siteRoot,
				name: "Surya Bintang Adventures",
				description: $t("meta.home.description"),
				publisher: {
					"@type": "TravelAgency",
					name: "Surya Bintang Adventures",
					url: siteRoot,
				},
			},
			{
				"@type": "WebPage",
				"@id": `${href}#webpage`,
				url: href,
				name: $t("meta.home.title"),
				description: $t("meta.home.description"),
				isPartOf: { "@id": `${siteRoot}#website` },
			},
		],
	};
});
const { categories, bikesCategories, homeArticles } =
	await useHomePageQueries();

const router = useRouter();

const hoveredIndex = ref<number | null>(null);
const selectedIndex = ref<number | null>(0);

// Function to open YouTube video in new tab
const openYouTubeVideo = () => {
	window.open("https://www.youtube.com/watch?v=G4Dq7UTg4To", "_blank");
};

// State to control showing all activities
const showAllActivities = ref(false);

// State for navigation arrows
const currentActivityIndex = ref(0);
const activitiesPerPage = 3;

// Testimonial data
const testimonials = ref([
	{
		text: "It was really amazing and great with kids even with 5 years old. Our guide was amazing and know how to deal with the kids and different age group. It’s very safe and not risky.",
		author: "Ali Al Lawati",
	},
	{
		text: "The rafting and ATV were both super exciting, and our guide was awesome—really friendly and easy to talk to. Totally recommended!",
		author: "Gito Otig",
	},
	{
		text: "We had so much fun rafting, great guides! We had a blast with him! Highly recommend this experience, don’t forget a change of clothes, we were soaked!",
		author: "Beth Aitken",
	},
]);

// Coupon data
const coupons = ref([
	{
		id: 1,
		title: "15% OFF Any Activity",
		description: "min. transaction Rp. 1.000K",
		code: "SURYABINTANGAJA",
	},
	{
		id: 2,
		title: "20% OFF Rafting",
		description: "min. transaction Rp. 2.000K",
		code: "RAFTING20",
	},
	{
		id: 3,
		title: "25% OFF ATV Adventure",
		description: "min. transaction Rp. 1.500K",
		code: "ATV25OFF",
	},
]);

// Handle book now button click
const handleBookNow = (slug: string) => {
	// Add your booking logic here
	router.push(`/activities/${slug}`);
};

// Handle see all activities toggle
const toggleShowAllActivities = () => {
	showAllActivities.value = !showAllActivities.value;
	// Reset navigation index when toggling
	if (showAllActivities.value) {
		currentActivityIndex.value = 0;
	}
};

// Navigation functions
const navigateLeft = () => {
	if (canNavigateLeft.value && !showAllActivities.value) {
		currentActivityIndex.value = Math.max(
			0,
			currentActivityIndex.value - activitiesPerPage,
		);
	}
};

const navigateRight = () => {
	if (canNavigateRight.value && !showAllActivities.value) {
		const totalCategories = categories.value.length;
		const maxIndex = Math.max(0, totalCategories - activitiesPerPage);
		currentActivityIndex.value = Math.min(
			maxIndex,
			currentActivityIndex.value + activitiesPerPage,
		);
	}
};

const displayProducts = computed(() => {
	if (showAllActivities.value) {
		return categories.value;
	}

	const startIndex = currentActivityIndex.value;
	const endIndex = startIndex + activitiesPerPage;
	return categories.value.slice(startIndex, endIndex);
});

const firstProductCategoryId = computed(() => categories.value[0]?.id);

// Computed properties for navigation state
const canNavigateLeft = computed(() => currentActivityIndex.value > 0);
const canNavigateRight = computed(() => {
	const totalCategories = categories.value.length;
	return currentActivityIndex.value + activitiesPerPage < totalCategories;
});

useSeoMeta({
	title: $t("meta.home.title"),
	description: $t("meta.home.description"),
	ogTitle: $t("meta.home.title"),
	twitterTitle: $t("meta.home.title"),
	ogDescription: $t("meta.home.description"),
	twitterDescription: $t("meta.home.description"),
	ogType: "website",
	ogUrl: href,
	twitterCard: "summary_large_image",
	keywords:
		"Adventure, Bali, Surya Bintang, ATV, Rafting , Indonesia Travel ,Bike Cycle Ride",
});

useServerSeoMeta({
	title: $t("meta.home.title"),
	description: $t("meta.home.description"),
	ogTitle: $t("meta.home.title"),
	twitterTitle: $t("meta.home.title"),
	ogDescription: $t("meta.home.description"),
	twitterDescription: $t("meta.home.description"),
	ogType: "website",
	ogUrl: href,
	twitterCard: "summary_large_image",
	keywords:
		"Adventure, Bali, Surya Bintang, ATV, Rafting, Indonesia Travel, Bike Cycle Ride",
});

useHead(() => ({
	link: [
		{
			rel: "canonical",
			href,
		},
	],
	script: [
		{
			type: "application/ld+json",
			children: JSON.stringify(structuredDataHome.value),
		},
		{
			type: "text/javascript",
			src: "https://static.elfsight.com/platform/platform.js",
			async: true,
		},
	],
}));
</script>

<style>
.swal2-title {
	line-height: 1 !important;
}

/* Hide scrollbar for horizontal scrolling */
.scrollbar-hide {
	-ms-overflow-style: none; /* Internet Explorer 10+ */
	scrollbar-width: none; /* Firefox */
}

.scrollbar-hide::-webkit-scrollbar {
	display: none; /* Safari and Chrome */
}

/* Smooth horizontal scrolling */
.overflow-x-auto {
	scroll-behavior: smooth;
}

/* Card hover effects for mobile */
@media (hover: hover) {
	.cursor-pointer:hover {
		transform: scale(1.02);
	}
}
</style>
